import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Formik, Field, Form, ErrorMessage } from "formik";
import * as Yup from "yup";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import axios from "axios";
import { useSelector } from "react-redux";
import { Row, Col, Button } from "react-bootstrap";
import Notification, {showToast} from "../../../../components/common/notification/Notification";
import { useNavigate } from "react-router-dom";

const MAX_DESCRIPTION_LENGTH = 2000;

const EditPostForm = () => {
  const { Id } = useParams();
  const [postData, setPostData] = useState(null);
  const [services, setServices] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [description, setDescription] = useState("");
  const [characterCount, setCharacterCount] = useState(0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  const baseUrl = process.env.REACT_APP_BASE_URL;
  const user = useSelector((state) => state.auth.user);
  // const token = user?.token;
  const token = user?.refreshToken;
  const navigate = useNavigate();

  // Fetch dropdown data and post data
  useEffect(() => {
    const fetchDropdowns = async () => {
      try {
        const serviceRes = await axios.get(`${baseUrl}/all/services`);
        setServices(serviceRes.data.services);

        const stateRes = await axios.post(
          `${baseUrl}/admin/states/1`,
          { page_number: 1, page_size: 20, order_by: "ASC" },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setStates(stateRes.data.states);
      } catch {
        setServices([]);
        setStates([]);
      }
    };

    const fetchPostData = async () => {
      try {
        const response = await axios.get(`${baseUrl}/property/lead/${Id}`,{
          headers: { Authorization: `Bearer ${token}` }
        });
        if (response.data.statusType === "SUCCESS") {
          const lead = response.data.lead;
          console.log(lead)
          setPostData(lead);
          setDescription(lead.description || "");
          setCharacterCount((lead.description || "").length);
        }
      } catch {
        setPostData(null);
      }
    };

    fetchDropdowns();
    fetchPostData();
  }, [baseUrl, token, Id]);

  // Fetch cities when state changes
  useEffect(() => {
    if (postData && postData.state) {
      fetchCities(postData.state);
    }
    // eslint-disable-next-line
  }, [postData?.state]);

  const fetchCities = async (stateId) => {
    if (!stateId) {
      setCities([]);
      return;
    }
    try {
      const res = await axios.post(
        `${baseUrl}/admin/cities/${stateId}`,
        { page_number: 1, page_size: 20, order_by: "ASC" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCities(res.data.cities || []);
    } catch {
      setCities([]);
    }
  };

  const validationSchema = Yup.object({
    service_id: Yup.string().required("Service is required"),
    service: Yup.string().required("Service name is required"),
    state: Yup.string().required("State is required"),
    state_name: Yup.string().required("State name is required"),
    city: Yup.string().required("City is required"),
    city_name: Yup.string().required("City name is required"),
    description: Yup.string()
      .required("Description is required")
      .max(MAX_DESCRIPTION_LENGTH, "Description too long"),
  });

  useEffect(() => {
    if (postData && states.length > 0) {
      const matchedState = states.find(s => s.name === postData.state_name);
      if (matchedState) {
        fetchCities(matchedState.id);
      }
    }
    // eslint-disable-next-line
  }, [postData, states]);

  // submit / update form
  const handleSubmit = async (values, { setSubmitting }) => {
    setPreviewData(values);
    setShowPreview(true);
    setSubmitting(false);
  }; 

  const handleFinalSubmit = async () => {
    try {
      // Build your payload
      const payload = {
        id: postData.id,
        service: previewData.service,
        state_name: previewData.state_name,
        city_name: previewData.city_name,
        description: previewData.description,
      };

      // Make the API call to update the post
      const response = await axios.put(
        `${baseUrl}/property/lead/edit`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data.statusType === "SUCCESS") {
        showToast("post updated successfully", {type: "success"});
        setTimeout(() => {
          navigate("/post");
        }, 3000);
      } else {
        showToast('Failed to update Post data', { type: 'error' });
      }
    } catch (err) {
      showToast('Failed to update Post data', { type: 'error' });
      console.log(err)
    }
  }; 

  if (!postData) return <p>Loading post data...</p>;

  const matchedService = services.find(s => s.name === postData.service);
  const matchedState = states.find(s => s.name === postData.state_name);
  const matchedCity = cities.find(c => c.name === postData.city_name);

  return (
    <div className="container my-5">
      <Notification/>
      <h2 className="text-primary fw-bold">Edit Post</h2>
      <div className="card shadow rounded-4 p-4 bg-light">
        <Formik
          enableReinitialize
          initialValues={{
            service_id: matchedService ? matchedService.id : "",
            service: postData.service || "",
            state: matchedState ? matchedState.id : "",
            state_name: postData.state_name || "",
            city: matchedCity ? matchedCity.id : "",
            city_name: postData.city_name || "",
            description: postData.description || "", 
          }}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        > 
          {({ setFieldValue, values, isSubmitting }) => (
            showPreview ? (
              <div className="preview-section">
                <h4 className="mb-4">Preview Your Post Update</h4>
                <div className="mb-3">
                  <strong>Service:</strong> {previewData.service}
                </div>
                <div className="mb-3">
                  <strong>Location:</strong> {previewData.city_name}, {previewData.state_name}
                </div>
                <div className="mb-3">
                  <strong>Description:</strong>
                  <div dangerouslySetInnerHTML={{ __html: previewData.description }} className="p-3 bg-white border rounded mt-2"></div>
                </div>
                <div className="d-flex justify-content-between mt-4">
                  <Button variant="secondary" onClick={() => setShowPreview(false)}>Back to Edit</Button>
                  <Button variant="success" onClick={handleFinalSubmit}>
                    Confirm & Update
                  </Button>
                </div>
              </div>
            ) : (
            <Form>
              <Row className="g-4 mb-4">
                <Col md={4}>
                  <div className="form-group">
                    <label htmlFor="service_id" className="form-label fw-semibold">Service</label>
                    <Field
                      as="select"
                      name="service_id"
                      id="service_id"
                      className="form-select"
                      value={values.service_id}
                      onChange={(e) => {
                        const selectedOption = e.target.selectedOptions[0];
                        const name = selectedOption.getAttribute("data-name");
                        setFieldValue("service_id", e.target.value);
                        setFieldValue("service", name);
                      }}
                    >
                      <option value="">Select a service</option>
                      {services.map((srv) => (
                        <option key={srv.id} value={srv.id} data-name={srv.name}>
                          {srv.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="service_id" component="div" className="text-danger mt-1 small" />
                  </div>
                </Col>

                <Col md={4}>
                  <div className="form-group">
                    <label htmlFor="state" className="form-label fw-semibold">State</label>
                    <Field
                      as="select"
                      name="state"
                      id="state"
                      className="form-select"
                      value={values.state}
                      onChange={async (e) => {
                        const selectedOption = e.target.selectedOptions[0];
                        const name = selectedOption.getAttribute("data-name");
                        const stateId = e.target.value;
                        setFieldValue("state", stateId);
                        setFieldValue("state_name", name);
                        await fetchCities(stateId);
                        setFieldValue("city", "");
                        setFieldValue("city_name", "");
                      }}
                    >
                      <option value="">Select a state</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id} data-name={state.name}>
                          {state.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="state" component="div" className="text-danger mt-1 small" />
                  </div>
                </Col>

                <Col md={4}>
                  <div className="form-group">
                    <label htmlFor="city" className="form-label fw-semibold">City</label>
                    <Field
                      as="select"
                      name="city"
                      id="city"
                      className="form-select"
                      value={values.city}
                      onChange={(e) => {
                        const selectedOption = e.target.selectedOptions[0];
                        const name = selectedOption.getAttribute("data-name");
                        setFieldValue("city", e.target.value);
                        setFieldValue("city_name", name);
                      }}
                    >
                      <option value="">Select a city</option>
                      {cities.map((city) => (
                        <option key={city.id} value={city.id} data-name={city.name}>
                          {city.name}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="city" component="div" className="text-danger mt-1 small" />
                  </div>
                </Col>
              </Row>

              <div className="mb-4">
                <label htmlFor="description" className="form-label fw-semibold">Description</label>
                <div className="border rounded-3 overflow-hidden">
                  <ReactQuill
                    theme="snow"
                    className="bg-white"
                    value={description}
                    onChange={(value) => {
                      setDescription(value);
                      setFieldValue("description", value);
                      setCharacterCount(value.length);
                    }}
                  />
                </div>
                <div className="d-flex justify-content-between mt-2 small text-muted">
                  <span>{characterCount}/{MAX_DESCRIPTION_LENGTH} characters</span>
                  {characterCount > MAX_DESCRIPTION_LENGTH && (
                    <span className="text-danger">Description too long</span>
                  )}
                </div>
                <ErrorMessage name="description" component="div" className="text-danger mt-1 small" />
              </div>

              <div className="d-flex justify-content-end mt-4">
                <Button
                  type="submit"
                  variant="primary"
                  className="px-4 py-2 fw-semibold"
                  disabled={isSubmitting || characterCount > MAX_DESCRIPTION_LENGTH}
                >
                  {isSubmitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span> Updating...
                    </>
                  ) : (
                    "Update"
                  )}
                </Button>
              </div>
            </Form>
            )
          )}
        </Formik>
        
      </div>
      <Link to='/post'>
        <Button className="mt-4">Back</Button>
      </Link>
    </div>
  );
};

export default EditPostForm;